import { useState, useMemo } from "react";

interface AssignSubCategoryModalProps {
    products: any[];
    subCategories: any[];
    categories: any[];
    isLoading: boolean;
    onClose: () => void;
    onAssign: (assignments: {
        productId: string;
        productTypeId: string;
        quantity: number;
        subProductTypeId: string;
    }[]) => void;
}

const AssignSubCategoryModal = ({
    products,
    subCategories,
    categories,
    isLoading,
    onClose,
    onAssign,
}: AssignSubCategoryModalProps) => {
    const [selectedCategoryId, setSelectedCategoryId] = useState("");
    const [selectedSubCategoryId, setSelectedSubCategoryId] = useState("");
    const [selectedProductIds, setSelectedProductIds] = useState<Set<string>>(
        () => new Set(),
    );
    const [searchQuery, setSearchQuery] = useState("");

    // Filter products by selected parent category
    const filteredProducts = useMemo(() => {
        if (!selectedCategoryId) return [];
        const selectedCat = categories.find(
            (c: any) => (c._id || c.id) === selectedCategoryId,
        );
        if (!selectedCat) return [];
        return products.filter((p: any) => {
            const matchesCategory = p.type === selectedCat.type;
            const matchesSearch =
                !searchQuery ||
                p.name?.toLowerCase().includes(searchQuery.toLowerCase());
            return matchesCategory && matchesSearch;
        });
    }, [products, categories, selectedCategoryId, searchQuery]);

    // Filter subcategories by selected parent category
    const filteredSubCategories = useMemo(() => {
        if (!selectedCategoryId) return [];
        return subCategories.filter((sc: any) => {
            const parentId =
                typeof sc.productTypeId === "object"
                    ? sc.productTypeId?._id
                    : sc.productTypeId;
            return parentId === selectedCategoryId;
        });
    }, [subCategories, selectedCategoryId]);

    const toggleProduct = (id: string) => {
        setSelectedProductIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const selectAll = () => {
        if (filteredProducts.length === 0) return;
        setSelectedProductIds((prev) => {
            const ids = filteredProducts.map((p: any) => p._id).filter(Boolean);
            const allSelected = ids.every((id: string) => prev.has(id));
            if (allSelected) return new Set();
            return new Set(ids);
        });
    };

    const handleAssign = () => {
        if (!selectedSubCategoryId || selectedProductIds.size === 0) return;
        const assignments = Array.from(selectedProductIds).map((productId) => {
            const product = products.find((p: any) => p._id === productId);
            return {
                productId,
                productTypeId: selectedCategoryId,
                quantity: product?.quantity || 0,
                subProductTypeId: selectedSubCategoryId,
            };
        });
        onAssign(assignments);
    };

    return (
        <div className="fixed inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50 z-40">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b">
                    <h2 className="font-bold text-lg">Assign Products to Subcategory</h2>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                        type="button"
                    >
                        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto p-6 space-y-4">
                    {/* Step 1: Select parent category */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            1. Select Parent Category
                        </label>
                        <select
                            value={selectedCategoryId}
                            onChange={(e) => {
                                setSelectedCategoryId(e.target.value);
                                setSelectedSubCategoryId("");
                                setSelectedProductIds(new Set());
                            }}
                            className="w-full border rounded-md p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-secondary"
                        >
                            <option value="">Choose a category...</option>
                            {categories.map((cat: any) => (
                                <option key={cat._id || cat.id} value={cat._id || cat.id}>
                                    {cat.type}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Step 2: Select target subcategory */}
                    {selectedCategoryId && (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                2. Select Target Subcategory
                            </label>
                            {filteredSubCategories.length === 0 ? (
                                <p className="text-sm text-gray-500 italic">
                                    No subcategories found for this category. Create one first.
                                </p>
                            ) : (
                                <select
                                    value={selectedSubCategoryId}
                                    onChange={(e) => setSelectedSubCategoryId(e.target.value)}
                                    className="w-full border rounded-md p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-secondary"
                                >
                                    <option value="">Choose a subcategory...</option>
                                    {filteredSubCategories.map((sc: any) => (
                                        <option key={sc._id} value={sc._id}>
                                            {sc.name}
                                        </option>
                                    ))}
                                </select>
                            )}
                        </div>
                    )}

                    {/* Step 3: Select products */}
                    {selectedCategoryId && selectedSubCategoryId && (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                3. Select Products to Assign ({selectedProductIds.size} selected)
                            </label>
                            {/* Search */}
                            <input
                                type="search"
                                placeholder="Search products..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full border rounded-md p-2 text-sm mb-2 focus:outline-none focus:ring-2 focus:ring-secondary"
                            />

                            {filteredProducts.length === 0 ? (
                                <p className="text-sm text-gray-500 italic py-4 text-center">
                                    No products found in this category.
                                </p>
                            ) : (
                                <>
                                    {/* Select all */}
                                    <button
                                        type="button"
                                        onClick={selectAll}
                                        className="text-xs text-secondary font-medium mb-2 hover:underline"
                                    >
                                        {filteredProducts.every((p: any) =>
                                            selectedProductIds.has(p._id),
                                        )
                                            ? "Deselect all"
                                            : "Select all"}
                                    </button>

                                    <div className="border rounded-md max-h-48 overflow-y-auto divide-y">
                                        {filteredProducts.map((product: any) => (
                                            <label
                                                key={product._id}
                                                className="flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 cursor-pointer"
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={selectedProductIds.has(product._id)}
                                                    onChange={() => toggleProduct(product._id)}
                                                    className="h-4 w-4 accent-secondary flex-shrink-0"
                                                />
                                                <div className="flex items-center gap-2 flex-1 min-w-0">
                                                    {product.image && (
                                                        <img
                                                            src={product.image}
                                                            alt=""
                                                            className="w-8 h-8 rounded object-cover flex-shrink-0"
                                                        />
                                                    )}
                                                    <div className="min-w-0">
                                                        <p className="text-sm font-medium truncate">
                                                            {product.name}
                                                        </p>
                                                        <p className="text-xs text-gray-500">
                                                            Qty: {product.quantity ?? "--"} • {product.measurement || ""}
                                                        </p>
                                                    </div>
                                                </div>
                                            </label>
                                        ))}
                                    </div>
                                </>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex justify-end gap-3 p-6 border-t bg-gray-50 rounded-b-lg">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 rounded-md border text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        disabled={
                            isLoading ||
                            !selectedSubCategoryId ||
                            selectedProductIds.size === 0
                        }
                        onClick={handleAssign}
                        className="px-5 py-2 rounded-md bg-secondary text-white text-sm font-medium hover:bg-opacity-90 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                    >
                        {isLoading
                            ? "Assigning..."
                            : `Assign ${selectedProductIds.size} Product${selectedProductIds.size !== 1 ? "s" : ""}`}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AssignSubCategoryModal;
